import React, {
  createContext,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import styles from './designerSinglePage.module.scss';
import readOnlyStyles from './singlePage.module.scss';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { requiredRule } from '../../../helpers/form/react-hook-form-helper.ts';
import { Tip } from '../../../components/uikit/Tip.tsx';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import clsx from 'clsx';
import { LexicalEditorIntegration } from './lexical/LexicalEditorIntegration.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import {
  CmsPageClient,
  CmsPageQuery,
  CmsPageVersionClient,
  CmsPageVersionQuery,
  Language,
  UserRole,
} from 'services/api/api-client.ts';
import { useWatch } from 'react-hook-form';
import i18n from 'i18next';
import HomeIcon from 'assets/icons/home-sweet-home.svg?react';
import DocumentIcon from 'assets/icons/document-icon.svg?react';
import ActiveDocumentIcon from 'assets/icons/active-document-icon.svg?react';
import GoBackIcon from 'assets/icons/arrow-long-left.svg?react';
import CopyIcon from 'assets/icons/copy.svg';
import OpenPage from 'assets/icons/open-page.svg';
import Settings from 'assets/icons/settings.svg?react';
import OpenPageDisabled from 'assets/icons/open-page.svg?react';
import { Links } from '../../../application/constants/links.ts';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import Cross from 'assets/icons/close-button-small.svg?react';
import { useNavigate } from 'react-router-dom';
import { RemindModalPageDesigner } from './RemindModalPageDesigner.tsx';
import { mergeRegister } from '@lexical/utils';
import {
  $getRoot,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  LexicalEditor,
} from 'lexical';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import LocalizationIcon from '../../../assets/icons/translation-needed.svg';
import { RemindModalContent } from './RemindModalContent.tsx';
import { formatDetailedDate } from '../../../helpers/abas-helper.ts';
import { setVersionAsActive } from './versionActions.ts';
import { CustomModal } from '../../../components/uikit/modal/CustomModal.tsx';
import { $isLocalizableTextNode } from './lexical/nodes/Localizable/LocalizableTextNode.tsx';
import { FeatureFlags } from '../../../application/constants/env-variables.ts';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import { AccessSettingsModal } from './AccessSettingsModal.tsx';
import { getCountries } from '../../../helpers/countries-helper.ts';

type SelectedCountry = {
  label: string;
  value: string;
};

export type Form = {
  pageTitle: string;
  pageUrl: string;
  shouldRedirect: boolean;
  defaultLanguage?: Language;
  selectedCountries: SelectedCountry[];
  selectedRoles: UserRole[];
};

type LanguageSelectorReminderModalProps = {
  isActive: boolean;
  pageEditorLanguage?: Language;
};

type StartValues = {
  title: string;
  url: string;
};

type PageDesignerContextType = {
  pageEditorLanguage: Language | null;
  setPageEditorLanguage: (language: Language) => void;
  pageTitle: string;
  defaultLanguage: Language | null;
  defaultLanguageSubmit: (language: Language) => void;
  handleTranslate: () => void;
  onEditorLanguageChange: (targetLanguage: Language) => void;
  setTitle: (title: string) => void;
  isVersionsModeActive: boolean;
  hideVersions: () => void;
  changeEditorState: (value?: string) => void;
  selectedPageVersion: SelectedPageVersion;
  setSelectedVersion: (selectedVersion: SelectedPageVersion) => void;
  versionsPageSize: number;
  increaseVersionPageSize: () => void;
  setIsLoading: (value: 'none' | 'translating' | 'fetching') => void;
};

export const PageDesignerContext = createContext<
  PageDesignerContextType | undefined
>(undefined);

const New = 'new';
const fakeRedirect = 'fakeRedirect';

const TitleTooltipBody = () => {
  return (
    <div className={clsx(styles.tooltipText, styles.rowContainer)}>
      <span>{i18n.t('Page.PageDesigner.title_tooltip_text')}</span>
      <span className={clsx(styles.rowContainer)}>
        <HomeIcon className={styles.homeIcon} />
        <span className={styles.fontWeight600}>
          {' '}
          / {i18n.t('Page.PageDesigner.page_name_example')}
        </span>
      </span>
    </div>
  );
};

const UrlTooltipBody = () => {
  return (
    <div className={styles.tooltipText}>
      {i18n.t('Page.PageDesigner.url_tooltip_text')}
      <span className={styles.greyColor}>
        https://leoworld.loewensteinmedical.com/
        <span className={styles.boldText}>your-url</span>
      </span>
    </div>
  );
};

export const VersionDetailsQueryKey = ['CmsPageVersionClient'];
export const versionsPageSizeNumber = 10;

const urlRegex = /^[a-zA-Z0-9\-]+$/;

type SelectedPageVersion = {
  lastUpdatedAt: Date | null;
  versionId: number | null;
  isActionsDisabled: boolean;
};

type ActionModals = {
  isSetActiveModalVisible: boolean;
  isPublishModalVisible: boolean;
  isAccessSettingsModalVisible: boolean;
};

export const None = 'none';
export const Translating = 'translating';
export const Fetching = 'fetching';

export const DesignerNewPage = () => {
  const i18n = useScopedTranslation('Page.PageDesigner');
  const [pageEditorLanguage, setPageEditorLanguage] = useState<Language | null>(
    null,
  );
  const [activeEditor, setActiveEditor] = useState<LexicalEditor | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [isLoading, setIsLoading] = useState<
    'none' | 'translating' | 'fetching'
  >(None);
  const [editorState, setEditorState] = useState<string | null>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isPublishedTooltipVisible, setIsPublishedTooltipVisible] =
    useState(false);
  const [languageSelectorReminderData, setLanguageSelectorReminderData] =
    useState<LanguageSelectorReminderModalProps>({
      isActive: false,
      pageEditorLanguage: undefined,
    });
  const [isVersionsModeActive, setIsVersionsModeActive] = useState(false);
  const [selectedPageVersion, setSelectedPageVersion] =
    useState<SelectedPageVersion>({
      lastUpdatedAt: null,
      versionId: null,
      isActionsDisabled: false,
    });
  const [versionsPageSize, setVersionsPageSize] = useState(
    versionsPageSizeNumber,
  );

  const [modalsVisibility, setModalVisibility] = useState<ActionModals>({
    isPublishModalVisible: false,
    isSetActiveModalVisible: false,
    isAccessSettingsModalVisible: false,
  });

  const shortTimerRef = useRef<NodeJS.Timeout>(null);
  const longTimerRef = useRef<NodeJS.Timeout>(null);
  const isAutosaving = useRef<boolean>(false);
  const timerDisabled = useRef(false);
  const latestVersionIdRef = useRef<number | null>(null);

  // we don't want to use autosave after two events which trigger onChangeEditorState inside OnChangePlugin
  // that's why we use this workaround. First such case - change of pageEditorLanguage (useEffect below),
  // second one - hideVersions(), there we set active version as current editor state, so we don't need to autosave it
  const disableAutosave = () => {
    timerDisabled.current = true;
    setTimeout(() => {
      timerDisabled.current = false;
    });
  };

  useEffect(() => {
    disableAutosave();
  }, [pageEditorLanguage]);

  const hideAllModals = () => {
    setModalVisibility({
      isPublishModalVisible: false,
      isSetActiveModalVisible: false,
      isAccessSettingsModalVisible: false,
    });
  };

  const increaseVersionPageSize = () => {
    setVersionsPageSize((prevState) => prevState + versionsPageSizeNumber);
  };

  const startValues = useRef<StartValues>({
    title: '',
    url: '',
  });
  const formRef = useRef<HTMLFormElement | null>(null);

  const routeParams = Links.Authorized.CustomPages.DesignerNewPage.useParams();
  const pageUrl = routeParams.url;
  const isNewPage = pageUrl === New;

  const navigate = useNavigate();

  const pageQuery = CmsPageQuery.useGetDetailsByUrlQuery(
    { url: pageUrl, getActiveVersion: true },
    {
      enabled: !isNewPage,
      throwOnError: false,
    },
  );

  const detailedPageVersionsQuery =
    CmsPageVersionQuery.useGetVersionDetailsQuery(
      {
        id: selectedPageVersion.versionId as number,
      },
      {
        enabled: !!selectedPageVersion.versionId,
        throwOnError: false,
        staleTime: 60 * 1000,
      },
    );

  useEffect(() => {
    void changeEditorState(detailedPageVersionsQuery.data?.content);
  }, [detailedPageVersionsQuery.data]);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (pageQuery.data?.defaultLanguage) {
      setPageEditorLanguage(pageQuery.data?.defaultLanguage);
    }
  }, [pageQuery.data?.defaultLanguage]);

  useEffect(() => {
    if (pageQuery.data?.versionId) {
      latestVersionIdRef.current = pageQuery.data.versionId;
    }
  }, [pageQuery.data?.versionId]);

  const language = i18n.i18n.language;

  const countryOptions = useMemo(
    () => getCountries(language.split('-')[0]),
    [language],
  );

  // allowedCountries has just countryCodes, here we add readable labels
  const namedAllowedCountries = countryOptions.filter((o) =>
    (pageQuery.data?.allowedCountries ?? []).includes(o.value),
  );

  const form = useAdvancedForm<Form>(
    async (data) => {
      if (!isAutosaving.current) {
        setIsLoading(Fetching);
        clearTimeouts();
      }

      if (isNewPage) {
        try {
          await CmsPageClient.create({
            showInMenu: true,
            title: data.pageTitle,
            url: data.pageUrl.toLowerCase(),
            content: editorState ?? '',
          });

          if (shouldRedirect) {
            void navigate(
              Links.Authorized.CustomPages.DesignerNewPage.link({
                url: data.pageUrl,
              }),
              {
                state: fakeRedirect,
              },
            );
          }
        } catch (err: any) {
          if (err.status === 409)
            form.setError('pageUrl', {
              message: i18n.t('url_already_exists'),
            });
        } finally {
          setCanUndo(false);
          setIsLoading(None);
        }
      } else {
        try {
          if (pageQuery.data?.id) {
            try {
              const titleEdited = form.formState.dirtyFields.pageTitle;

              const result = await CmsPageClient.patchById(
                pageQuery.data?.id,
                {
                  showInMenu: true,
                  title: titleEdited ? data.pageTitle : undefined,
                  url: data.pageUrl,
                  content: editorState ?? '',
                  defaultLanguage: data.defaultLanguage,
                },
                // language param only affects title translation
                // if language == null then the original title will change
                pageEditorLanguage ?? undefined,
                isAutosaving.current,
              );

              latestVersionIdRef.current = result.versionId;

              await queryClient.invalidateQueries({
                queryKey: CmsPageQuery.searchQueryKey(),
              });

              if (shouldRedirect) {
                void navigate(
                  Links.Authorized.CustomPages.DesignerNewPage.link({
                    url: data.pageUrl,
                  }),
                  {
                    state: fakeRedirect,
                  },
                );

                if (!isAutosaving.current) {
                  await pageQuery.refetch();
                }
              }

              if (data.defaultLanguage) {
                setIsTooltipVisible(true);
                setTimeout(() => setIsTooltipVisible(false), 6000);
                form.setValue('defaultLanguage', undefined);
              }
            } catch (err: any) {
              if (err.status === 409)
                form.setError('pageUrl', {
                  message: i18n.t('url_already_exists'),
                });
            }
          }
        } finally {
          if (!isAutosaving.current) {
            setCanUndo(false);
          }

          setIsLoading(None);
          isAutosaving.current = false;
        }
      }
    },
    {
      defaultValues: {
        pageUrl: '',
        pageTitle: '',
        shouldRedirect: true,
        defaultLanguage: undefined,
        selectedCountries: namedAllowedCountries,
        selectedRoles: pageQuery.data?.allowedRoles ?? [],
      },
    },
  );

  const [
    pageTitle,
    pageUrlForm,
    shouldRedirect,
    selectedRoles,
    selectedCountries,
  ] = useWatch({
    control: form.control,
    name: [
      'pageTitle',
      'pageUrl',
      'shouldRedirect',
      'selectedRoles',
      'selectedCountries',
    ],
  });

  const changeAccessSettings = async () => {
    if (pageQuery.data?.id) {
      const result = await CmsPageClient.patchById(
        pageQuery.data.id,
        {
          allowedRoles: selectedRoles.filter((role) => role !== UserRole.Admin),
          allowedCountries: selectedCountries.map((c) => c.value),
        },
        // language param only affects title translation
        // if language == null then the original title will change
        pageEditorLanguage ?? undefined,
      );

      latestVersionIdRef.current = result.versionId;
    }
  };

  const clearTimeouts = () => {
    if (shortTimerRef.current) clearTimeout(shortTimerRef.current);
    if (longTimerRef.current) clearTimeout(longTimerRef.current);

    shortTimerRef.current = null;
    longTimerRef.current = null;
  };

  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, []);

  const setSelectedVersion = (selectedVersion: SelectedPageVersion) => {
    setSelectedPageVersion(selectedVersion);
  };

  const showVersions = async () => {
    if (!!shortTimerRef.current || !!longTimerRef.current) {
      setIsLoading(Fetching);
      clearTimeouts();
      isAutosaving.current = true;
      await handleSubmitForm();
    }

    activeEditor?.setEditable(false);
    setIsVersionsModeActive(true);

    setSelectedVersion({
      versionId: pageQuery.data?.versionId ?? null,
      lastUpdatedAt: pageQuery.data?.contentLastUpdatedAt ?? null,
      isActionsDisabled: true,
    });
  };

  const hideVersions = () => {
    disableAutosave();
    setIsVersionsModeActive(false);
    activeEditor?.setEditable(true);
    void changeEditorState(pageQuery.data?.content);
    setVersionsPageSize(versionsPageSizeNumber);
    setCanUndo(false);
  };

  const publishVersion = async () => {
    try {
      hideAllModals();

      if (!isSaveButtonDisabled) {
        const isSaved = await handleSubmitForm();
        if (!isSaved) {
          return;
        }
      }

      const versionIdToPublish =
        latestVersionIdRef.current ?? pageQuery.data?.versionId;

      if (!versionIdToPublish) {
        throw new Error('Version id should be number');
      }

      setIsLoading(Fetching);
      await CmsPageVersionClient.publishVersion(versionIdToPublish);

      await queryClient.invalidateQueries({
        queryKey: CmsPageQuery.getDetailsByUrlQueryKey({
          url: pageUrl,
          getActiveVersion: true,
        }),
      });

      setIsPublishedTooltipVisible(true);
      setTimeout(() => setIsPublishedTooltipVisible(false), 3000);
    } catch (e) {
      console.error('Failed to publish version', e);
    } finally {
      setIsLoading(None);
    }
  };

  const changeEditorState = async (newState?: string) => {
    try {
      if (activeEditor && newState) {
        const parsedEditorState = activeEditor.parseEditorState(newState);
        // we need this to avoid this error
        // flushSync was called from inside a lifecycle method
        await Promise.resolve();
        activeEditor.setEditorState(parsedEditorState);

        const defaultLanguage = pageQuery.data?.defaultLanguage;

        activeEditor.update(() => {
          if (!pageEditorLanguage || !defaultLanguage) {
            return;
          }

          const root = $getRoot();
          root.getAllTextNodes().forEach((node) => {
            if ($isLocalizableTextNode(node)) {
              node.setPageDesignerLanguage(pageEditorLanguage);

              const translation =
                node.getTranslation(pageEditorLanguage) ||
                node.getTranslation(defaultLanguage);

              const defaultTranslation = node.getTranslation(defaultLanguage);

              if (!defaultTranslation) {
                node.setTranslations(defaultLanguage, node.getTextContent());
              }

              if (translation && node.getTextContent() !== translation) {
                node.setTextContent(translation);
              }
            }
          });
        });
      } else {
        activeEditor?.update(() => {
          $getRoot().clear();
        });
      }
    } catch (e) {
      console.error('Failed to set new editor state', e);
    }
  };

  const translation = pageEditorLanguage
    ? pageQuery.data?.titleTranslations.find(
        (translation) => translation.language === pageEditorLanguage,
      )
    : undefined;

  const isTitleEdited = form.formState.dirtyFields.pageTitle;
  const isTranslationMissingOrExpired =
    !translation || translation.isTranslationExpired;

  const titleNeedsTranslation =
    !isTitleEdited &&
    isTranslationMissingOrExpired &&
    pageQuery.data?.defaultLanguage !== pageEditorLanguage &&
    !isNewPage;

  useEffect(() => {
    const title = translation?.translatedText ?? pageQuery.data?.title;

    const url = pageQuery.data?.url;

    if (title) {
      startValues.current = { ...startValues.current, title: title };
      form.reset(
        { ...form.getValues(), pageTitle: title },
        { keepDirty: false },
      );
    }

    if (url) {
      startValues.current = { ...startValues.current, url: url };
      form.setValue('pageUrl', url);
    }
  }, [pageQuery.data, pageEditorLanguage]);

  const handleSubmitForm = async (
    e?: FormEvent<HTMLFormElement>,
    isSubmitFromModal?: boolean,
  ) => {
    e?.preventDefault();
    if (isSubmitFromModal) {
      form.setValue('shouldRedirect', false);
    }

    const result = await form.trigger();

    if (pageUrlForm.length < 3) {
      form.setError('pageUrl', {
        message: i18n.t('url_too_short_error'),
      });

      return false;
    }

    if (!urlRegex.test(pageUrlForm)) {
      form.setError('pageUrl', {
        message: i18n.t('url_contains_wrong_symbols'),
      });

      return false;
    }

    if (result) {
      await form.handleSubmitDefault();
    }

    return result;
  };

  const autosave = (ref: React.RefObject<NodeJS.Timeout | null>) => {
    if (isLoading === None && !isVersionsModeActive) {
      isAutosaving.current = true;
      ref.current = null;
      void handleSubmitForm();
    }
  };

  const handleEditorChange = (newState: string, hasNodeChanged: boolean) => {
    setEditorState(newState);

    if (
      !hasNodeChanged ||
      isNewPage ||
      timerDisabled.current ||
      isVersionsModeActive
    ) {
      return;
    }

    if (shortTimerRef.current) {
      clearTimeout(shortTimerRef.current);
    }

    shortTimerRef.current = setTimeout(() => autosave(shortTimerRef), 15000);

    if (!longTimerRef.current) {
      longTimerRef.current = setTimeout(() => autosave(longTimerRef), 60000);
    }
  };

  useEffect(() => {
    if (pageQuery.data?.content) {
      try {
        const parsed = JSON.parse(pageQuery.data.content);
        if (parsed.root.children.length) {
          setEditorState(pageQuery.data.content);
        }
      } catch {}
    }
  }, [pageQuery.data]);

  const content = useMemo(() => {
    const data = pageQuery.data;

    // if you create page, then just click inside editor and save the page,
    // children array will be empty which cause an error inside editor.
    // that's why we should replace such content with null
    try {
      if (data) {
        const parsed = JSON.parse(data.content);

        if (!parsed || !parsed.root.children.length) {
          return null;
        }

        return data.content;
      }
      return null;
    } catch (e) {
      return null;
    }
  }, [pageQuery.data]);

  const { url: startUrl, title: startTitle } = startValues.current;

  const isRemindModalActive =
    (pageTitle !== startTitle || pageUrlForm !== startUrl || canUndo) &&
    !isVersionsModeActive;

  const handleTranslate = async () => {
    if (pageQuery.data?.id) {
      try {
        setIsLoading(Translating);
        await CmsPageClient.translatePage(
          pageQuery.data?.id,
          editorState ?? '',
          pageEditorLanguage ?? undefined,
          titleNeedsTranslation,
        );
        setIsLoading(None);
        void pageQuery.refetch();
      } catch (e) {
        setIsLoading(None);
      }
    }
  };

  const onEditorLanguageChange = (targetLanguage: Language) => {
    if (
      form.formState.dirtyFields.pageTitle ||
      form.formState.dirtyFields.pageUrl
    )
      setLanguageSelectorReminderData({
        isActive: true,
        pageEditorLanguage: targetLanguage,
      });
    else setPageEditorLanguage(targetLanguage);
  };

  useEffect(() => {
    if (activeEditor) {
      return mergeRegister(
        activeEditor.registerCommand<boolean>(
          CAN_UNDO_COMMAND,
          (payload) => {
            if (timerDisabled.current) {
              return false;
            }
            setCanUndo(payload);
            return false;
          },
          COMMAND_PRIORITY_CRITICAL,
        ),
      );
    }
  }, [activeEditor]);

  const closeRemindModal = () => {
    setLanguageSelectorReminderData({
      isActive: false,
    });
  };

  const defaultLanguageSubmit = (language: Language) => {
    if (formRef.current) {
      form.setValue('defaultLanguage', language);
      formRef.current.requestSubmit();
    }
  };

  const setTitle = (value: string) => {
    form.setValue('pageTitle', value, {
      shouldDirty: true,
    });
    form.setError('pageTitle', {
      message: undefined,
    });
  };

  const contextValue = useMemo(
    () => ({
      pageEditorLanguage,
      setPageEditorLanguage,
      defaultLanguage: pageQuery.data?.defaultLanguage ?? null,
      defaultLanguageSubmit,
      pageTitle,
      handleTranslate,
      onEditorLanguageChange,
      setTitle,
      isVersionsModeActive,
      hideVersions,
      changeEditorState,
      selectedPageVersion,
      setSelectedVersion,
      versionsPageSize,
      increaseVersionPageSize,
      setIsLoading,
    }),
    [
      pageEditorLanguage,
      pageQuery.data?.defaultLanguage,
      pageTitle,
      isVersionsModeActive,
      selectedPageVersion,
      versionsPageSize,
    ],
  );

  const setActiveVersionData = {
    versionId: selectedPageVersion.versionId,
    setEditorState,
    setIsVersionsModeActive,
    handleSubmitDefault: form.handleSubmitDefault,
    activeEditor,
  };

  const isFetchingPageVersions = useIsFetching({
    queryKey: CmsPageVersionQuery.searchQueryKey(),
  });

  const isFetchingVersionDetails = useIsFetching({
    queryKey: VersionDetailsQueryKey,
  });

  const isVersionActionsDisabled = selectedPageVersion.isActionsDisabled;
  const pageUrlReadOnly = window.location.origin + '/' + startUrl;

  const isSaveButtonDisabled =
    isVersionsModeActive ||
    !isRemindModalActive ||
    (isNewPage && (!pageTitle || !pageUrlForm));

  return (
    <Loading
      loading={
        isLoading !== 'none' ||
        pageQuery.isFetching ||
        !!isFetchingPageVersions ||
        !!isFetchingVersionDetails
      }
      title={isLoading === 'translating' ? i18n.t('translating') : undefined}
      textClassName={styles.loaderTextClassName}
    >
      <div className={styles.relative}>
        <Breadcrumbs
          pageName={pageTitle ? pageTitle : i18n.t('new_page')}
          customLinkForHomeIcon={Links.Authorized.Admin.route}
        />
        <form ref={formRef} onSubmit={handleSubmitForm}>
          <div className={clsx(styles.rowContainer, styles.spaceBetween)}>
            <div className={clsx(styles.rowContainer, styles.columnGap5)}>
              <div className={styles.helperText}>{i18n.t('title')}</div>
              <div className={styles.marginRight5}>
                <Tip tooltipBody={<TitleTooltipBody />} />
              </div>
              {isVersionsModeActive ? (
                <div
                  className={clsx(
                    styles.text,
                    styles.width333,
                    styles.marginLeft17,
                  )}
                >
                  {pageTitle}
                </div>
              ) : (
                <Input
                  {...form.register(
                    'pageTitle',
                    requiredRule(i18n.t('required_error')),
                  )}
                  errorText={form.formState.errors.pageTitle?.message}
                  onChange={(e) => setTitle(e.target.value)}
                  className={styles.input}
                  containerClassName={styles.marginRight10}
                  endAdornment={
                    <div className={styles.rowContainer10}>
                      {pageTitle && (
                        <IconButton
                          type={'destroy'}
                          onClick={() => form.setValue('pageTitle', '')}
                        >
                          <Cross />
                        </IconButton>
                      )}
                      {titleNeedsTranslation && (
                        <Tip
                          tooltipText={i18n.t('translation_needed')}
                          customImage={LocalizationIcon}
                          className={styles.marginTop5}
                        />
                      )}
                    </div>
                  }
                  endAdornmentClassname={styles.endAdornmentClassname}
                />
              )}
              <div className={styles.helperText}>{i18n.t('url')}</div>
              <div className={styles.marginRight5}>
                <Tip tooltipBody={<UrlTooltipBody />} />
              </div>
              {isVersionsModeActive ? (
                <div
                  className={clsx(
                    styles.text,
                    styles.width333,
                    styles.marginLeft17,
                  )}
                >
                  {pageUrlForm}
                </div>
              ) : (
                <div className={styles.rowContainer}>
                  <Input
                    {...form.register(
                      'pageUrl',
                      requiredRule(i18n.t('required_error')),
                    )}
                    errorText={form.formState.errors.pageUrl?.message}
                    onChange={(e) => {
                      form.setValue('pageUrl', e.target.value.toLowerCase(), {
                        shouldDirty: true,
                      });
                      form.setError('pageUrl', {
                        message: undefined,
                      });
                    }}
                    className={styles.input}
                    containerClassName={styles.marginRight10}
                    endAdornment={
                      <div>
                        {pageUrlForm && (
                          <IconButton
                            type={'destroy'}
                            onClick={() => form.setValue('pageUrl', '')}
                          >
                            <Cross />
                          </IconButton>
                        )}
                      </div>
                    }
                    endAdornmentClassname={styles.endAdornmentClassname}
                  />
                  {!isNewPage && (
                    <>
                      <Tip
                        tooltipText={i18n.t('copy')}
                        customImage={CopyIcon}
                        className={clsx(
                          styles.backgroundUnset,
                          styles.marginRight10,
                        )}
                        onClick={() =>
                          navigator.clipboard.writeText(pageUrlReadOnly)
                        }
                      />
                      {pageQuery.data?.hasPublishedVersion ? (
                        <Tip
                          tooltipText={i18n.t('go_to') + pageUrlReadOnly}
                          customImage={OpenPage}
                          className={styles.backgroundUnset}
                          onClick={() => window.open(`/${startUrl}`, '_blank')}
                        />
                      ) : (
                        <OpenPageDisabled className={styles.disabledImage} />
                      )}
                    </>
                  )}
                  {!isNewPage && (
                    <ActionText
                      text={i18n.t('access_settings')}
                      Svg={Settings}
                      className={styles.marginLeft17}
                      onClickHandler={() =>
                        setModalVisibility((prevState) => ({
                          ...prevState,
                          isAccessSettingsModalVisible: true,
                        }))
                      }
                    />
                  )}
                </div>
              )}
            </div>
            <div className={clsx(styles.rowContainer, styles.columnGap24)}>
              {FeatureFlags.isPageDesignerVersionsEnabled() && !isNewPage && (
                <div
                  className={clsx(
                    styles.rowContainer,
                    styles.columnGap8,
                    styles.pointer,
                    isVersionsModeActive && styles.exitVersionModeContainer,
                    isVersionsModeActive && styles.hover,
                  )}
                  onClick={isVersionsModeActive ? hideVersions : showVersions}
                >
                  {isVersionsModeActive ? (
                    <>
                      <GoBackIcon />
                      <div
                        className={clsx(
                          styles.tooltipText,
                          styles.documentTextBlue,
                        )}
                      >
                        {i18n.t('exit_versions_mode')}
                      </div>
                    </>
                  ) : (
                    <div
                      className={clsx(
                        styles.rowContainer,
                        styles.columnGap8,
                        styles.versionModeButton,
                      )}
                    >
                      <DocumentIcon />
                      <div
                        className={clsx(
                          styles.tooltipText,
                          styles.documentText,
                        )}
                      >
                        {i18n.t('versions_mode')}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className={clsx(styles.rowContainer, styles.columnGap12)}>
                <Button
                  color={ButtonColor.Secondary}
                  type={'submit'}
                  title={i18n.t('save')}
                  disabled={isSaveButtonDisabled}
                />
                {!isNewPage ? (
                  isVersionsModeActive ? (
                    <Tip
                      textBody={
                        <Button
                          title={i18n.t('publish')}
                          disabled={true}
                          onClick={() =>
                            setModalVisibility((prevState) => ({
                              ...prevState,
                              isPublishModalVisible: true,
                            }))
                          }
                        />
                      }
                      tooltipBody={
                        <div>{i18n.t('disabled_publish_button_tip')}</div>
                      }
                      tooltipClassName={styles.tooltipPublishButton}
                    />
                  ) : (
                    <Button
                      title={i18n.t('publish')}
                      onClick={() =>
                        setModalVisibility((prevState) => ({
                          ...prevState,
                          isPublishModalVisible: true,
                        }))
                      }
                    />
                  )
                ) : null}
              </div>
            </div>
          </div>
        </form>
        {!pageQuery.isFetching && (
          <PageDesignerContext.Provider value={contextValue}>
            <div
              className={clsx(
                styles.container,
                styles.marginTop25,
                isVersionsModeActive && readOnlyStyles.container,
              )}
            >
              <LexicalEditorIntegration
                isPageDesigner={true}
                editable={true}
                isNewPage={isNewPage}
                onChange={(state, hasNodeChanged) => {
                  handleEditorChange(
                    JSON.stringify(state.toJSON()),
                    hasNodeChanged,
                  );
                }}
                initialState={content}
                setActiveEditor={setActiveEditor}
              />
            </div>
          </PageDesignerContext.Provider>
        )}
        {isVersionsModeActive && (
          <div className={clsx(styles.deviceListActionsContainer)}>
            <div className={styles.rowContainer}>
              <div className={clsx(styles.fontWeight600, styles.versionTime)}>
                {i18n.t('viewing_version_from')}
              </div>
              <div className={clsx(styles.fontWeight600, styles.versionTime)}>
                {formatDetailedDate(
                  selectedPageVersion.lastUpdatedAt ?? new Date(),
                  i18n.i18n,
                )}
              </div>
            </div>
            <div className={clsx(styles.rowContainer, styles.columnGap24)}>
              <div
                className={clsx(
                  styles.setActiveContainer,
                  isVersionActionsDisabled && styles.cursorDefault,
                  isVersionActionsDisabled && styles.disabledActiveContainer,
                )}
                onClick={
                  isVersionActionsDisabled
                    ? undefined
                    : () =>
                        setModalVisibility((prevState) => ({
                          ...prevState,
                          isSetActiveModalVisible: true,
                        }))
                }
              >
                <ActiveDocumentIcon
                  className={clsx(
                    isVersionActionsDisabled && styles.disabledIcon,
                  )}
                />
                <div>{i18n.t('set_version_active')}</div>
              </div>
            </div>
          </div>
        )}
      </div>
      {modalsVisibility.isAccessSettingsModalVisible && (
        <AccessSettingsModal
          closeModal={hideAllModals}
          changeAccessSettings={changeAccessSettings}
          form={form}
          countryOptions={countryOptions}
        />
      )}
      {isTooltipVisible && (
        <Tooltip
          closeTooltip={() => setIsTooltipVisible(false)}
          bodyContent={<div>{i18n.t('default_language_tooltip')}</div>}
          className={styles.tooltip}
          showCancelButton={true}
        />
      )}
      {isPublishedTooltipVisible && (
        <Tooltip
          closeTooltip={() => setIsPublishedTooltipVisible(false)}
          bodyContent={<div>{i18n.t('page_is_published')}</div>}
          className={styles.tooltip}
          showCancelButton={true}
        />
      )}
      <RemindModalPageDesigner
        isActive={isRemindModalActive}
        saveChangesFunc={handleSubmitForm}
      />
      {languageSelectorReminderData.isActive && (
        <RemindModalContent
          onClose={() => {
            closeRemindModal();
          }}
          onCancel={() => {
            closeRemindModal();
            setPageEditorLanguage(
              languageSelectorReminderData.pageEditorLanguage!,
            );
          }}
          onSubmit={async () => {
            closeRemindModal();
            await handleSubmitForm();
            setPageEditorLanguage(
              languageSelectorReminderData.pageEditorLanguage!,
            );
          }}
        />
      )}
      {modalsVisibility.isSetActiveModalVisible && (
        <CustomModal
          isOpen={true}
          onSubmit={() => {
            void setVersionAsActive(setActiveVersionData);
            hideAllModals();
            setVersionsPageSize(versionsPageSizeNumber);
          }}
          onCancel={hideAllModals}
          cancelButtonText={i18n.t('cancel')}
          submitButtonText={i18n.t('set_as_active')}
          titleText={i18n.t('set_version_active')}
          bodyText={i18n.t('set_version_active_modal_body')}
          containerClassName={styles.modalBodyMinWidth480}
          textClassName={styles.lineHeight24}
          submitButtonType={ButtonColor.Primary}
        />
      )}
      {modalsVisibility.isPublishModalVisible && (
        <CustomModal
          isOpen={true}
          onSubmit={publishVersion}
          onCancel={hideAllModals}
          cancelButtonText={i18n.t('cancel')}
          submitButtonText={i18n.t('publish')}
          titleText={i18n.t('publish_version')}
          bodyText={i18n.t('publish_version_modal_body')}
          containerClassName={styles.modalBodyMinWidth480}
          textClassName={styles.lineHeight24}
          submitButtonType={ButtonColor.Primary}
        />
      )}
    </Loading>
  );
};
